import React, { useEffect, useState, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, Tag, X, Check, Ban } from 'lucide-react';
import { couponsAPI } from '../services/api';

const EMPTY_FORM = {
  code: '',
  discount_type: 'percentage',
  discount_value: '',
  buy_quantity: '1',
  get_quantity: '1',
  max_uses: '',
  min_order_value: '',
  max_discount_amount: '',
  expires_at: '',
  is_active: true,
  description: '',
};

const BXGY_PRESETS = [
  { label: 'Buy 1 Get 1', buy: 1, get: 1 },
  { label: 'Buy 2 Get 1', buy: 2, get: 1 },
  { label: 'Buy 3 Get 1', buy: 3, get: 1 },
];

const Coupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadCoupons = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await couponsAPI.getAll();
      setCoupons(res.coupons || []);
    } catch (err) {
      setError(err.message || 'Failed to load coupons');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (coupon) => {
    setEditingId(coupon.id);
    setForm({
      code: coupon.code || '',
      discount_type: coupon.discountType || 'percentage',
      discount_value: coupon.discountValue ?? '',
      buy_quantity: coupon.buyQuantity ?? '1',
      get_quantity: coupon.getQuantity ?? '1',
      max_uses: coupon.maxUses ?? '',
      min_order_value: coupon.minOrderValue ?? '',
      max_discount_amount: coupon.maxDiscountAmount ?? '',
      expires_at: coupon.expiresAt ? String(coupon.expiresAt).slice(0, 10) : '',
      is_active: coupon.isActive !== false,
      description: coupon.description || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.code.trim()) {
      setFormError('Coupon code is required');
      return;
    }

    if (form.discount_type === 'bxgy') {
      if (!form.buy_quantity || Number(form.buy_quantity) < 1) {
        setFormError('Buy quantity must be at least 1');
        return;
      }
      if (!form.get_quantity || Number(form.get_quantity) < 1) {
        setFormError('Get free quantity must be at least 1');
        return;
      }
    } else {
      if (!form.discount_value || Number(form.discount_value) <= 0) {
        setFormError('Discount value must be greater than 0');
        return;
      }
      if (form.discount_type === 'percentage' && Number(form.discount_value) > 100) {
        setFormError('Percentage discount cannot exceed 100');
        return;
      }
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      discount_type: form.discount_type,
      discount_value: form.discount_type === 'bxgy' ? Number(form.get_quantity) : Number(form.discount_value),
      buy_quantity: form.discount_type === 'bxgy' ? Number(form.buy_quantity) : 0,
      get_quantity: form.discount_type === 'bxgy' ? Number(form.get_quantity) : 0,
      max_uses: form.max_uses === '' ? 0 : Number(form.max_uses),
      min_order_value: form.min_order_value === '' ? 0 : Number(form.min_order_value),
      max_discount_amount: form.max_discount_amount === '' ? 0 : Number(form.max_discount_amount),
      expires_at: form.expires_at || null,
      is_active: form.is_active,
      description: form.description.trim(),
    };

    try {
      setSaving(true);
      if (editingId) {
        await couponsAPI.update(editingId, payload);
      } else {
        await couponsAPI.create(payload);
      }
      await loadCoupons();
      closeForm();
    } catch (err) {
      setFormError(err.message || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"? This cannot be undone.`)) {
      return;
    }
    try {
      await couponsAPI.delete(coupon.id);
      await loadCoupons();
    } catch (err) {
      alert(err.message || 'Failed to delete coupon');
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await couponsAPI.update(coupon.id, { is_active: !coupon.isActive });
      await loadCoupons();
    } catch (err) {
      alert(err.message || 'Failed to update coupon');
    }
  };

  const formatDiscount = (coupon) => {
    if (coupon.discountType === 'bxgy') {
      return `Buy ${coupon.buyQuantity || 1} Get ${coupon.getQuantity || 1} Free`;
    }
    if (coupon.discountType === 'percentage') {
      const cap = coupon.maxDiscountAmount > 0 ? ` (max ₹${coupon.maxDiscountAmount})` : '';
      return `${coupon.discountValue}% off${cap}`;
    }
    return `₹${coupon.discountValue} off`;
  };

  const formatUsage = (coupon) => {
    const used = coupon.usedCount || 0;
    if (!coupon.maxUses || coupon.maxUses === 0) {
      return `${used} / ∞`;
    }
    return `${used} / ${coupon.maxUses}`;
  };

  const isExpired = (coupon) =>
    coupon.expiresAt && new Date(coupon.expiresAt) < new Date();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
          <p className="text-gray-600 mt-1">Create and manage discount codes for your customers</p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={loadCoupons}
            disabled={loading}
            className={`inline-flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
            <span>Refresh</span>
          </button>
          <button
            onClick={openCreate}
            className="inline-flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            <Plus size={20} />
            <span>New Coupon</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Coupons Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading coupons...</p>
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No coupons yet</h3>
            <p className="text-gray-500 mb-4">Create your first discount code to get started.</p>
            <button
              onClick={openCreate}
              className="inline-flex items-center space-x-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <Plus size={20} />
              <span>Create Coupon</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Discount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Min Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {coupons.map((coupon) => (
                  <tr key={coupon.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-mono font-semibold text-gray-900">{coupon.code}</span>
                      {coupon.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{coupon.description}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{formatDiscount(coupon)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {coupon.minOrderValue > 0 ? `₹${coupon.minOrderValue}` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{formatUsage(coupon)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {coupon.expiresAt ? (
                        <span className={isExpired(coupon) ? 'text-red-600' : ''}>
                          {new Date(coupon.expiresAt).toLocaleDateString()}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {coupon.isActive && !isExpired(coupon) ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {isExpired(coupon) ? 'Expired' : 'Inactive'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => toggleActive(coupon)}
                          className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                          title={coupon.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {coupon.isActive ? <Ban size={16} /> : <Check size={16} />}
                        </button>
                        <button
                          onClick={() => openEdit(coupon)}
                          className="p-1.5 text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded"
                          title="Edit"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(coupon)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingId ? 'Edit Coupon' : 'Create Coupon'}
              </h2>
              <button onClick={closeForm} className="p-2 text-gray-500 hover:text-gray-800 rounded-lg">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-600">{formError}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code *</label>
                <input
                  type="text"
                  value={form.code}
                  onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                  placeholder="e.g. WELCOME10"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount Type *</label>
                <select
                  value={form.discount_type}
                  onChange={(e) => handleChange('discount_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                  <option value="bxgy">Buy X Get Y Free</option>
                </select>
              </div>

              {form.discount_type === 'bxgy' ? (
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {BXGY_PRESETS.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            buy_quantity: String(preset.buy),
                            get_quantity: String(preset.get),
                          }))
                        }
                        className="px-3 py-1.5 text-sm rounded-lg border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Buy quantity (X) *</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.buy_quantity}
                        onChange={(e) => handleChange('buy_quantity', e.target.value)}
                        placeholder="2"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Get free (Y) *</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={form.get_quantity}
                        onChange={(e) => handleChange('get_quantity', e.target.value)}
                        placeholder="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500">
                    Example: Buy {form.buy_quantity || 'X'} Get {form.get_quantity || 'Y'} Free —
                    cheapest free item(s) are discounted for every complete set of{' '}
                    {(Number(form.buy_quantity) || 0) + (Number(form.get_quantity) || 0) || 'X+Y'} items.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {form.discount_type === 'percentage' ? 'Percentage *' : 'Amount (₹) *'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.discount_value}
                      onChange={(e) => handleChange('discount_value', e.target.value)}
                      placeholder={form.discount_type === 'percentage' ? '10' : '100'}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  {form.discount_type === 'percentage' && (
                    <div className="col-span-2 sm:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Max Discount Cap (₹) <span className="text-gray-400">(optional)</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.max_discount_amount}
                        onChange={(e) => handleChange('max_discount_amount', e.target.value)}
                        placeholder="Leave blank for no cap"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Uses <span className="text-gray-400">(0 = unlimited)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={form.max_uses}
                    onChange={(e) => handleChange('max_uses', e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Min Order Value (₹) <span className="text-gray-400">(optional)</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.min_order_value}
                    onChange={(e) => handleChange('min_order_value', e.target.value)}
                    placeholder="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiry Date <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="date"
                  value={form.expires_at}
                  onChange={(e) => handleChange('expires_at', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Internal note shown to customers"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">Active (customers can use this coupon)</span>
              </label>

              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium ${
                    saving ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {saving ? 'Saving...' : editingId ? 'Update Coupon' : 'Create Coupon'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupons;
